import {
  Description,
  Email,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import type { ProviderTemplateResponse } from '@store/generated/organizations';

import { useGetTemplatesQuery } from '../store/api';

// Mapping of template IDs to user-friendly names
const TEMPLATE_DISPLAY_NAMES: Record<string, string> = {
  'progress_note': 'Progress Note',
  'intake_note': 'Intake Note',
  'soap_note': 'SOAP Note',
  'discharge_summary': 'Discharge Summary',
  'treatment_plan': 'Treatment Plan',
  'consultation_note': 'Consultation Note',
  'psychiatric_evaluation': 'Psychiatric Evaluation',
  'therapy_note': 'Therapy Note',
  'medication_management': 'Medication Management',
  'group_therapy': 'Group Therapy Note',
};

export function ReadOnlyTemplatesSection() {
  // API Query
  const {
    data: templatesData,
    isLoading,
  } = useGetTemplatesQuery();

  const templates = templatesData?.templates || [];

  // Get display name for a template
  const getTemplateDisplayName = (template: ProviderTemplateResponse) => {
    // Use custom display name if available
    if (template.display_name) {
      return template.display_name;
    }

    // Check if it's a standard template ID
    const standardName = TEMPLATE_DISPLAY_NAMES[template.template_id];
    if (standardName) {
      return standardName;
    }

    // For user templates (e.g., "u:abc-123"), extract a readable name
    if (template.template_id.startsWith('u:')) {
      return 'Custom Template';
    }

    // Fallback to making the ID readable
    return template.template_id
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Available Note Templates"
        subheader="Templates configured for your organization"
        action={
          templates.length > 0 && (
            <Chip
              label={`${templates.length} template${templates.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info">
            No templates are currently available for your organization. Contact your billing administrator to configure templates.
          </Alert>
        ) : (
          <>
            <Alert severity="info" icon={<Email />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Need a custom template?</strong> Get in touch with us at{' '}
                <a href="mailto:contact@jotpsych.com" style={{ color: 'inherit', fontWeight: 'bold' }}>
                  contact@jotpsych.com
                </a>{' '}
                and we'll add it to your organization for all of your providers.
              </Typography>
            </Alert>
            <List>
            {templates.map((template) => (
              <ListItem
                key={template.template_id}
                sx={{
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemIcon>
                  <Description color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight="medium">
                      {getTemplateDisplayName(template)}
                    </Typography>
                  }
                  secondary={
                    template.created_at && (
                      <Typography variant="caption" color="text.secondary">
                        Available since {new Date(template.created_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
}